/**
 * Main Dashboard - Enhanced API Client with Intelligent Caching
 * Implements production-grade caching strategies with stale-while-revalidate
 */

import CachedAPIClient from './cached-api';
import { getOfflineManager } from './offline';
import PrefetchManager from './prefetch';
import { getAuthToken, saveAuthToken } from './cookies';

// API base URL - adjust if your backend runs on a different port
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';

// Initialize cached API client
export const api = new CachedAPIClient(API_BASE_URL, {
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup auth token management
api.setAuthTokenGetter(() => getAuthToken());
api.setAuthErrorHandler(() => {
  window.dispatchEvent(new CustomEvent('prepxai:open-auth-modal', {
    detail: { mode: 'login', options: { redirectTo: window.location.pathname || '/dashboard' } },
  }));
});

// Initialize prefetch manager
const prefetchManager = new PrefetchManager(api);
prefetchManager.setupLinkHoverPrefetch();
prefetchManager.setupConnectionAwarePrefetch();

// Initialize offline manager
const offlineManager = getOfflineManager();
offlineManager.setCallbacks({
  onOnline: () => {
    console.log('App is back online');
  },
  onOffline: () => {
    console.log('App is offline - showing cached data');
  },
});

/**
 * Hook API client methods to track cache updates
 * The CachedAPIClient interceptor already handles:
 *   - Caching successful responses
 *   - Reporting success/failure to OfflineManager
 *   - Falling back to cache on network errors (for GET requests)
 * This wrapper adds endpoint-level tracking for offline status display.
 */
const originalGet = api.get.bind(api);
api.get = async (url, config = {}) => {
  try {
    const response = await originalGet(url, config);

    // Track when we last got fresh data for this endpoint
    if (response && response.meta && !response.meta.cached) {
      offlineManager.trackUpdate(url);
    }

    return response;
  } catch (error) {
    // If originalGet failed (network error or cache miss), try a final fallback
    // We try this regardless of the reported 'online' status to catch transient failures
    const fallback = await offlineManager.getCachedFallback(url, config.params);
    if (fallback) {
      console.warn(`[Offline] Using emergency fallback for ${url}`);
      return {
        data: fallback.data,
        status: 200,
        statusText: 'OK (Offline Cache Fallback)',
        config,
        meta: {
          cached: true,
          isOffline: true,
          lastUpdated: fallback.lastUpdated,
          message: fallback.message,
        },
      };
    }
    
    // If still no data, re-throw
    throw error;
  }
};

/**
 * Get all available interview types
 * @returns {Promise} API response with interview types
 */
export const getInterviewTypes = async () => {
  try {
    const response = await api.get('/api/interview-types');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to fetch interview types' };
  }
};

/**
 * Register a new user
 * @param {Object} userData - { email, username, password }
 * @returns {Promise} API response with user data and token
 */
export const registerUser = async (userData) => {
  try {
    const response = await api.post('/auth/register', userData);
    if (response.data.token) {
      saveAuthToken(response.data.token);
    }
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNREFUSED' || error.message === 'Network Error') {
      throw { error: 'Cannot connect to server. Please make sure the backend is running on port 8081.' };
    }
    throw error.response?.data || { error: error.message || 'Registration failed' };
  }
};

/**
 * Login user
 * @param {Object} credentials - { emailOrUsername, password }
 * @returns {Promise} API response with user data and token
 */
export const loginUser = async (credentials) => {
  try {
    const response = await api.post('/auth/login', credentials);
    if (response.data.token) {
      saveAuthToken(response.data.token);
    }
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNREFUSED' || error.message === 'Network Error') {
      throw { error: 'Cannot connect to server. Please make sure the backend is running on port 8081.' };
    }
    throw error.response?.data || { error: error.message || 'Login failed' };
  }
};

/**
 * Get interview detail by session ID
 */
export const getInterviewDetail = async (sessionId) => {
  const response = await api.axiosInstance.get(`/api/interviews/${sessionId}`, {
    params: { _ts: Date.now() },
  });
  offlineManager.reportSuccess();
  offlineManager.trackUpdate(`/api/interviews/${sessionId}`);
  if (response.data && typeof response.data === 'object') {
    return { ...response.data, __httpStatus: response.status };
  }
  return response.data;
};

/**
 * Get interview benchmark by session ID
 */
export const getInterviewBenchmark = async (sessionId) => {
  try {
    const response = await api.get(`/api/interviews/${sessionId}/benchmark`);
    return response.data;
  } catch (err) {
    console.error('Benchmark fetch failed:', err);
    return null;
  }
};

/**
 * Get current user profile
 */
export const getCurrentUser = async (token = null) => {
  try {
    const config = {};
    if (token) {
      config.headers = { Authorization: `Bearer ${token}` };
    }
    const response = await api.get('/auth/me', config);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message };
  }
};

/**
 * Get extended profile for settings
 */
export const getProfile = async () => {
  const response = await api.get('/auth/profile');
  return response.data;
};

/**
 * Update user profile
 */
export const updateProfile = async (data) => {
  const response = await api.post('/auth/profile', data);
  return response.data;
};

/**
 * Update username
 */
export const updateUsername = async (username) => {
  const response = await api.patch('/auth/username', { username });
  return response.data;
};

/**
 * Update notification settings
 */
export const updateNotifications = async (settings) => {
  const response = await api.put('/auth/notifications', settings);
  return response.data;
};

/**
 * Upload resume
 */
export const uploadResume = async (formData) => {
  const response = await api.post('/auth/resume', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Get payment history
 */
export const getPayments = async () => {
  const response = await api.get('/api/payments');
  return response.data;
};

/**
 * Get user coin transaction history
 */
export const getCoinTransactions = async (limit = 50, offset = 0) => {
  const response = await api.get('/api/users/me/coin-transactions', { params: { limit, offset } });
  return response.data;
};

/**
 * Get user coin transaction history (Admin)
 */
export const adminGetUserCoinTransactions = async (userId, limit = 50, offset = 0) => {
  const response = await api.get(`/api/admin/users/${userId}/coin-transactions`, { params: { limit, offset } });
  return response.data;
};

/**
 * Send delete account OTP
 */
export const sendDeleteOtp = async () => {
  const response = await api.post('/api/users/me/delete-otp');
  return response.data;
};

/**
 * Delete account
 */
export const deleteAccount = async (otp) => {
  const response = await api.delete('/api/users/me', { data: { otp } });
  return response.data;
};

const filenameFromContentDisposition = (contentDisposition, fallback) => {
  if (!contentDisposition) return fallback;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/"/g, ''));
  }

  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] || fallback;
};

/**
 * Download current user's data export document
 */
export const downloadMyData = async () => {
  try {
    const response = await api.axiosInstance.get('/api/users/me/export', {
      responseType: 'blob',
    });

    return {
      blob: response.data,
      filename: filenameFromContentDisposition(
        response.headers?.['content-disposition'],
        `prepxai-data-export-${new Date().toISOString().slice(0, 10)}.md`,
      ),
    };
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to download data export' };
  }
};

/**
 * Get referral program data
 */
export const getReferralData = async () => {
  const response = await api.get('/api/referrals/code');
  return response.data;
};

/**
 * Export enhanced API client and utilities
 */
export default api;

export const prefetch = (route) => prefetchManager.prefetchForRoute(route);
export const getCacheStats = () => api.getCacheStats();
export const clearCache = () => api.clearCache();
export const getOfflineStatus = () => ({
  isOnline: offlineManager.getIsOnline(),
  stats: offlineManager.getStats(),
});

/**
 * Create interview session
 * Requires Authorization: Bearer <token>
 * @param {Object} payload - { role, duration, company, difficulty, type }
 * @param {String} token - JWT token
 */
export const createInterviewSession = async (payload, token) => {
  try {
    // include userId from cookie if available (for now front-end sends it too)
    const { getUserId } = await import('./cookies');
    const userId = getUserId();
    const body = userId ? { ...payload, userId } : payload;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await api.post('/create-session', body, {
      headers,
    });
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNREFUSED' || error.message === 'Network Error') {
      throw { error: 'Cannot connect to server. Please make sure the backend is running on port 8081.' };
    }
    throw error.response?.data || { error: error.message || 'Failed to create session' };
  }
};

/**
 * Get API base URL
 */
export const getApiBaseUrl = () => {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';
};

/**
 * Get interview session details
 * @param {String} sessionId
 * @returns {Promise} API response with session data
 */
export const getSession = async (sessionId) => {
  if (!sessionId) throw new Error('sessionId is required');
  try {
    const { getAuthToken } = await import('./cookies');
    const token = getAuthToken();
    const response = await api.get(`/session/${sessionId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to fetch session' };
  }
};

/**
 * End an interview session and retrieve the generated report
 * @param {String} sessionId
 * @param {Array} integrityEvents
 * @param {{code: String, language: String}|null} codeSnapshot
 */
export const endInterviewSession = async (sessionId, integrityEvents = [], codeSnapshot = null) => {
  if (!sessionId) throw new Error('sessionId is required');
  try {
    const { getAuthToken } = await import('./cookies');
    const token = getAuthToken();
    const requestConfig = {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
      timeout: 300000,
    };
    const hasCodeSnapshot = codeSnapshot && typeof codeSnapshot.code === 'string';
    const response = integrityEvents.length > 0 || hasCodeSnapshot
      ? await api.post(`/end-session/${sessionId}`, {
        integrity_events: integrityEvents,
        ...(hasCodeSnapshot ? {
          final_code: codeSnapshot.code,
          final_code_language: codeSnapshot.language || '',
        } : {}),
      }, requestConfig)
      : await api.get(`/end-session/${sessionId}`, requestConfig);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to end session' };
  }
};

/**
 * Initiate Google OAuth login
 * Redirects to Google OAuth page
 */
export const initiateGoogleLogin = () => {
  const API_BASE_URL = getApiBaseUrl();
  // Redirect to backend OAuth endpoint
  window.location.href = `${API_BASE_URL}/auth/google`;
};

/**
 * Request password reset OTP
 * @param {String} email
 */
export const forgotPassword = async (email) => {
  try {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to request reset' };
  }
};

/**
 * Reset password using OTP
 * @param {Object} data - { email, otp, newPassword }
 */
export const resetPassword = async (data) => {
  try {
    const response = await api.post('/auth/reset-password', data);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to reset password' };
  }
};

/**
 * Verify user email with OTP
 * @param {Object} data - { otp }
 */
export const verifyEmail = async (data) => {
  try {
    const { getAuthToken } = await import('./cookies');
    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await api.post('/auth/verify-email', data, { headers });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Verification failed' };
  }
};

/**
 * Resend verification email
 */
export const resendVerificationEmail = async () => {
  try {
    const { getAuthToken } = await import('./cookies');
    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await api.post('/auth/resend-verification', {}, { headers });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to resend email' };
  }
};

// Add response interceptor for global error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if network error or server down
    if (error.code === 'ECONNREFUSED' || error.message === 'Network Error') {
      const customError = { error: 'Cannot connect to server. Please make sure the backend is running on port 8081.' };
      // You can dispatch a global toast/alert event here if you have a store or event bus
      // For now, we'll let the customError cascade
      return Promise.reject(customError);
    }

    // Generic "Something went wrong" for 500 errors if no specific message
    if (error.response && error.response.status >= 500) {
      // Dispatch global error event that UI can listen to, or rely on toast if integrated
      // dispatchEvent(new CustomEvent('global-api-error', { detail: 'Something went wrong. Please try again later.' }));
      const apiError = error.response.data || {};
      if (!apiError.error && !apiError.message) {
        return Promise.reject({ ...apiError, error: 'Something went wrong. Please try again later.' });
      }
    }
    // Check for blocked user error message
    if (error.response?.data?.error?.includes("blocked")) {
      window.location.href = '/blocked';
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Public Blog API endpoints
export const getPublicBlogs = async () => {
  const response = await api.get('/api/blogs');
  return response.data;
};
export const getBlogBySlug = async (slug) => {
  const response = await api.get(`/api/blogs/${slug}`);
  return response.data;
};
export const likeBlog = async (slug) => {
  const response = await api.post(`/api/blogs/${slug}/like`);
  return response.data;
};
export const unlikeBlog = async (slug) => {
  const response = await api.post(`/api/blogs/${slug}/unlike`);
  return response.data;
};

// Blog Comments API
export const getBlogComments = async (slug) => {
  const response = await api.get(`/api/blogs/${slug}/comments`);
  return response.data;
};
export const postBlogComment = async (slug, content, parentId = null) => {
  const response = await api.post(`/api/blogs/${slug}/comments`, { content, parent_id: parentId });
  return response.data;
};
export const adminDeleteBlogComment = async (commentId) => {
  const response = await api.delete(`/api/admin/blogs/comments/${commentId}`);
  return response.data;
};

export const subscribeNewsletter = async (email) => {
  const response = await api.post('/api/newsletter/subscribe', { email });
  return response.data;
};

export const submitInterviewFeedback = async (sessionId, feedbackData) => {
  if (!sessionId) throw new Error('sessionId is required');
  try {
    const response = await api.post(`/api/interviews/${sessionId}/feedback`, feedbackData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to submit feedback' };
  }
};

export const submitInterviewIssueReport = async (sessionId, issueType, description) => {
  try {
    const payload = {
      session_id: sessionId, // Optional, can be null
      issue_type: issueType,
      description: description
    };
    const response = await api.post(`/api/interviews/report-issue`, payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to submit issue report' };
  }
};

/**
 * Execute code against interview question test cases
 * @param {Object} data - { question_id, code, language }
 */
export const executeInterviewCode = async (data) => {
  try {
    const response = await api.post('/api/interviews/execute', data);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Execution failed' };
  }
};

/**
 * Share a sandbox session
 * @param {Object} data - { code, language }
 */
export const shareSandboxSession = async (data) => {
  try {
    const response = await api.post('/api/sandbox/share', data);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to share session' };
  }
};

/**
 * Get a shared sandbox session by ID
 * @param {String} id 
 */
export const getSandboxSession = async (id) => {
  try {
    const response = await api.get(`/api/sandbox/share/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to get session' };
  }
};



// ── Resume Analyzer (Public) ───────────────────────────────────────────────
export const analyzeResume = async (formData) => {
  try {
    const response = await api.post('/api/resume/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 min — LLM can be slow
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Analysis failed. Please try again.' };
  }
};

export const getResumeReport = async (reportId) => {
  try {
    const response = await api.get(`/api/resume/reports/${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to load resume report.' };
  }
};

export const generateOptimizedResume = async (data) => {
  try {
    const response = await api.post('/api/resume/generate-optimized', data, {
      timeout: 300000, // 5 min timeout
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to generate optimized resume.' };
  }
};

export const exportResumePDF = async (text) => {
  const response = await api.post('/api/resume/export-pdf', { text }, {
    responseType: 'blob',
    timeout: 60000,
  });
  return response.data;
};


// ── Forum API ──────────────────────────────────────────────────────────
export const getForumPosts = async (params = {}) => {
  const response = await api.get('/api/forum/posts', { params });
  return response.data;
};

export const getForumPostDetail = async (id) => {
  const response = await api.get(`/api/forum/posts/${id}`);
  return response.data;
};

export const getForumComments = async (postId) => {
  const response = await api.get(`/api/forum/posts/${postId}/comments`);
  return response.data;
};

export const createForumPost = async (data) => {
  const response = await api.post('/api/forum/posts', data);
  return response.data;
};

export const updateForumPost = async (id, data) => {
  const response = await api.put(`/api/forum/posts/${id}`, data);
  return response.data;
};

export const postForumComment = async (postId, data) => {
  const response = await api.post(`/api/forum/posts/${postId}/comments`, data);
  return response.data;
};

export const castForumVote = async (data) => {
  const response = await api.post('/api/forum/vote', data);
  return response.data;
};

export const getForumStats = async () => {
  const response = await api.get('/api/forum/stats');
  return response.data;
};

export const getMyForumPosts = async () => {
  const response = await api.get('/api/forum/my-posts');
  return response.data;
};

export const getTrendingForumPosts = async (limit = 5) => {
  const response = await api.get(`/api/forum/trending?limit=${limit}`);
  return response.data;
};

export const getSimilarForumPosts = async (id) => {
  const response = await api.get(`/api/forum/posts/${id}/similar`);
  return response.data;
};

// ── Admin Forum Moderation ─────────────────────────────────────────────
export const adminDeleteForumPost = async (id) => {
  const response = await api.delete(`/api/admin/forum/posts/${id}`);
  return response.data;
};

export const adminDeleteForumComment = async (id) => {
  const response = await api.delete(`/api/admin/forum/comments/${id}`);
  return response.data;
};

// ── Dashboard & Progress APIs ──────────────────────────────────────────
export const getInterviews = async (limit = 50, offset = 0) => {
  const response = await api.get('/api/interviews', { params: { limit, offset } });
  return response.data;
};

export const getInterviewStats = async () => {
  const response = await api.get('/api/interviews/stats');
  return response.data;
};

export const getActivityStats = async () => {
  const response = await api.get('/api/activity/stats');
  return response.data;
};

export const getAchievements = async () => {
  const response = await api.get('/api/achievements');
  return response.data;
};

export const getMyPlans = async () => {
  const response = await api.get('/api/my-plans');
  return response.data;
};

export const getPlanSchedule = async (planId) => {
  const response = await api.get(`/api/my-plans/${planId}/schedule`);
  return response.data;
};

export const getScheduledInterviews = async () => {
  const response = await api.get('/api/scheduled-interviews');
  return response.data;
};

export const getQuizCertificates = async () => {
  const response = await api.get('/api/learn/certificates');
  return response.data;
};

export const getStrengths = async () => {
  const response = await api.get('/api/strengths');
  return response.data;
};

export const getWeakAreas = async () => {
  const response = await api.get('/api/weak-areas');
  return response.data;
};

export const getSystemStatus = async () => {
  const response = await api.get('/api/system/status');
  return response.data;
};

// ── Company API ────────────────────────────────────────────────────────
export const getCompanies = async () => {
  const response = await api.get('/api/companies');
  return response.data;
};

export const getCompanyStats = async (companyName) => {
  const { getAuthToken } = await import('./cookies');
  const token = getAuthToken();
  const response = await api.get(`/api/interviews/company-stats?company=${encodeURIComponent(companyName)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return response.data;
};

export const getMyFeatureFlags = async () => {
  const response = await api.get('/api/feature-flags/my');
  return response.data;
};

// ── Institution B2B — Student-side (consumer-authed) ───────────────────────
// A student takes institution-assigned interviews inside this same consumer
// dashboard, signed in with their own account. These endpoints sit behind the
// consumer AuthMiddleware (NOT the institution admin tenant gate) and use the
// distinct /api/institution-assignments prefix. The server derives the student
// from their JWT and links roster rows by email, so a student can only ever see
// or start their OWN assignments. No coins are charged — the institution
// reserved a credit at assign time.

/**
 * List the signed-in student's institution-assigned interviews (newest first).
 * @returns {Promise<{assignments: Array}>}
 */
export const listMyInstitutionAssignments = async () => {
  try {
    const response = await api.get('/api/institution-assignments');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to load assigned interviews' };
  }
};

/**
 * Start (or resume) one assigned interview. Reuses the shared interview engine.
 * Returns { session_id, expires_at } on a fresh launch, or { session_id, resumed:true }
 * when an in-progress session is handed back.
 * @param {String} assignmentId
 */
export const startInstitutionAssignment = async (assignmentId) => {
  if (!assignmentId) throw new Error('assignmentId is required');
  try {
    const { getAuthToken } = await import('./cookies');
    const token = getAuthToken();
    const response = await api.post(
      `/api/institution-assignments/${encodeURIComponent(assignmentId)}/start`,
      {},
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: error.message || 'Failed to start assigned interview' };
  }
};
export const getPublicFeatureFlags = async () => {
    const response = await api.get('/api/feature-flags/public');
    return response.data;
};
